import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Orders from './pages/Orders';
import NewOrder from './pages/NewOrder';
import OrderDetail from './pages/OrderDetail';
import Products from './pages/Products';
import PurchaseOrders from './pages/PurchaseOrders';
import ActivityLog from './pages/ActivityLog';
import Vendors from './pages/Vendors';
import Notes from './pages/Notes';
import Suppliers from './pages/Suppliers';
import Analytics from './pages/Analytics';
import Dispatched from './pages/Dispatched';
import Production from './pages/Production';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ownerOnly items are hidden from the sidebar (and blocked from direct URL access) for Admin
const NAV = [
  { to:'/', label:'Dashboard', icon:'⊞', ownerOnly:true },
  { to:'/orders', label:'Orders', icon:'📋', ownerOnly:true },
  { to:'/production', label:'Production', icon:'🏗️' },
  { to:'/customers', label:'Customers', icon:'🏢', ownerOnly:true },
  { to:'/products', label:'Products', icon:'🔩', ownerOnly:true },
  { to:'/purchase-orders', label:'Purchase Orders', icon:'🛒' },
  { to:'/suppliers', label:'Suppliers', icon:'🚚', ownerOnly:true },
  { to:'/vendors', label:'Job Work Vendors', icon:'🏭', ownerOnly:true },
  { to:'/analytics', label:'Analytics', icon:'📊', ownerOnly:true },
  { to:'/dispatched', label:'Dispatched', icon:'🚢', ownerOnly:true },
  { to:'/notes', label:'Notes Log', icon:'📝', ownerOnly:true },
  { to:'/activity-log', label:'Activity Log', icon:'🕵️', ownerOnly:true },
];

function Sidebar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isActive = (to) => to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to);
  return (
    <div style={{ width:220, minHeight:'100vh', background:'#1a1f2e', display:'flex', flexDirection:'column', flexShrink:0, borderRight:'1px solid #2d3347' }}>
      <div style={{ padding:'20px 16px 16px', borderBottom:'1px solid #2d3347' }}>
        <div style={{ color:'#fff', fontWeight:700, fontSize:15, letterSpacing:.3 }}>Byldr ERP</div>
        <div style={{ color:'#7a8499', fontSize:11, marginTop:2 }}>P.P. Impex India</div>
      </div>
      <nav style={{ flex:1, padding:'6px 0', overflowY:'auto' }}>
        {NAV.filter(n => !n.ownerOnly || user?.role === 'owner').map(n => (
          <Link key={n.to} to={n.to} style={{
            display:'flex', alignItems:'center', gap:9, padding:'9px 16px',
            color: isActive(n.to) ? '#fff' : '#8892a4',
            background: isActive(n.to) ? 'rgba(59,130,246,.12)' : 'transparent',
            textDecoration:'none', fontSize:13,
            borderLeft: isActive(n.to) ? '3px solid #3b82f6' : '3px solid transparent',
          }}>
            <span style={{ fontSize:14 }}>{n.icon}</span>{n.label}
          </Link>
        ))}
      </nav>
      <div style={{ padding:'12px 16px', borderTop:'1px solid #2d3347' }}>
        <div style={{ color:'#e8ecf4', fontSize:13, fontWeight:500 }}>{user?.name}</div>
        <div style={{ color:'#7a8499', fontSize:11, textTransform:'uppercase', letterSpacing:.8 }}>{user?.role}</div>
        <button onClick={logout} style={{ marginTop:8, fontSize:12, color:'#7a8499', background:'none', border:'none', cursor:'pointer', padding:0 }}>Sign out</button>
      </div>
    </div>
  );
}

function Layout({ children }) {
  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#f5f6fa' }}>
      <Sidebar />
      <main style={{ flex:1, padding:'24px 28px', overflowY:'auto', minWidth:0 }}>{children}</main>
    </div>
  );
}

function Private({ children, ownerOnly }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (ownerOnly && user.role !== 'owner') return <Navigate to="/production" />;
  return <Layout>{children}</Layout>;
}

export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('byldr_user')); } catch { return null; }
  });

  const login = (token, userData) => {
    localStorage.setItem('byldr_token', token);
    localStorage.setItem('byldr_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('byldr_token');
    localStorage.removeItem('byldr_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Private ownerOnly><Dashboard /></Private>} />
          <Route path="/orders" element={<Private ownerOnly><Orders /></Private>} />
          <Route path="/orders/new" element={<Private ownerOnly><NewOrder /></Private>} />
          <Route path="/orders/:id" element={<Private ownerOnly><OrderDetail /></Private>} />
          <Route path="/production" element={<Private><Production /></Private>} />
          <Route path="/customers" element={<Private ownerOnly><Customers /></Private>} />
          <Route path="/customers/:id" element={<Private ownerOnly><CustomerDetail /></Private>} />
          <Route path="/products" element={<Private ownerOnly><Products /></Private>} />
          <Route path="/purchase-orders" element={<Private><PurchaseOrders /></Private>} />
          <Route path="/suppliers" element={<Private ownerOnly><Suppliers /></Private>} />
          <Route path="/vendors" element={<Private ownerOnly><Vendors /></Private>} />
          <Route path="/analytics" element={<Private ownerOnly><Analytics /></Private>} />
          <Route path="/dispatched" element={<Private ownerOnly><Dispatched /></Private>} />
          <Route path="/notes" element={<Private ownerOnly><Notes /></Private>} />
          <Route path="/activity-log" element={<Private ownerOnly><ActivityLog /></Private>} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
